import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Spinner, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf, faSync, faFilter } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getLocationTrackerData } from "../../../api/driver/locationTrackerApi";
import { getGroupConsignments } from "../../../api/warehouse/groupConsignmentApi";
import "./TripTemperatureReport.css";

/* ------------------------------------------------------------------ */
/* Types & helpers                                                     */
/* ------------------------------------------------------------------ */

/** One grid row = one consignment + ONE temperature log of that trip. */
interface ReportRow {
  id: string;
  groupKey: string; // same for every log row of one consignment
  group: any;
  consignment: any;
  item: any;
  temp: any | null; // this row's tracker log (null = no data for the trip)
  hay: string; // lower-case text of all columns, built lazily for the global search
  hayN: string; // same text with only letters/digits, for loose matching (MH 12-AB → mh12ab)
}

type FilterKind = "list" | "number" | "date" | "temp" | "none";

interface Column {
  key: string;
  label: string;
  width: number;
  align?: "left" | "right" | "center";
  filter?: FilterKind; // default "list"
  text: (r: ReportRow, index: number) => string;
  num?: (r: ReportRow) => number | null; // for number / temp filters
  date?: (r: ReportRow) => Date | null; // for date filters
  render?: (r: ReportRow, index: number) => React.ReactNode;
}

interface ColFilter {
  list?: string[]; // exact values to show
  min?: string;
  max?: string;
  from?: string;
  to?: string;
  levels?: string[]; // temperature levels
}

const ROW_H = 24; // fixed row height (px) so only the visible rows need to be rendered
const OVERSCAN = 12;

const STATUS_TEXT: Record<string, string> = {
  "0": "Ordered",
  "1": "In Progress",
  "2": "Delivered",
  "3": "Out of Delivery",
  "4": "Forced Delivery",
};

const STATUS_CLASS: Record<string, string> = {
  "0": "st-info",
  "1": "st-primary",
  "2": "st-success",
  "3": "st-warning",
  "4": "st-danger",
};

const TEMP_LEVEL_OPTIONS = [
  { value: "normal", label: "Normal (≤ 5°C)" },
  { value: "warning", label: "Warning (5–10°C)" },
  { value: "high", label: "High (> 10°C)" },
  { value: "nodata", label: "No Data" },
];

const toNum = (v: any): number | null => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};

const fmtTemp = (v: any) => {
  const n = toNum(v);
  return n === null ? "-" : `${n.toFixed(1)}°C`;
};

const tempClass = (v: any) => {
  const n = toNum(v);
  if (n === null) return "t-none";
  if (n > 10) return "t-high";
  if (n > 5) return "t-mid";
  return "t-ok";
};

const tempLevel = (n: number | null) => (n === null ? "nodata" : n > 10 ? "high" : n > 5 ? "warning" : "normal");

const toDate = (d?: string): Date | null => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const fmtDateTime = (d?: string) => (d ? new Date(d).toLocaleString("en-GB").replace(", ", " ") : "-");const statusText = (s: any) => STATUS_TEXT[String(s)] || "Unknown";
const normReg = (s?: string) => (s || "").replace(/[^a-z0-9]/gi, "").toUpperCase();

/** Collect every string value (length >= 8) inside an object; used to find the id the tracker refers to. */
const collectStrings = (obj: any, out: Set<string>, depth = 0, skipKeys: string[] = []) => {
  if (obj == null || depth > 3) return;
  if (typeof obj === "string") {
    if (obj.length >= 8) out.add(obj);
    return;
  }
  if (Array.isArray(obj)) {
    obj.forEach((o) => collectStrings(o, out, depth + 1, skipKeys));
    return;
  }
  if (typeof obj === "object") {
    Object.keys(obj).forEach((k) => {
      if (skipKeys.indexOf(k) === -1) collectStrings(obj[k], out, depth + 1, skipKeys);
    });
  }
};

const pushTo = (map: Map<string, any[]>, key: string, val: any) => {
  const list = map.get(key);
  if (list) list.push(val);
  else map.set(key, [val]);
};

const isActive = (f: ColFilter) =>
  !!((f.list && f.list.length) || f.min || f.max || f.from || f.to || (f.levels && f.levels.length));

const splitTerms = (s: string) =>
  s
    .split(/[,;|\r\n\t]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);

// Keeps only letters/digits (and the column separator so matches can't span two columns)
const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9\u0001]/g, "");

/* ------------------------------------------------------------------ */
/* Columns: everything in one row                                      */
/* ------------------------------------------------------------------ */

const COLUMNS: Column[] = [
  { key: "sr", label: "Sr", width: 60, align: "center", filter: "none", text: (_r, i) => String(i + 1) },
  { key: "groupId", label: "Group Consignment ID", width: 160, text: (r) => String(r.temp?.sConsignmentId ?? r.group.grpConsignmentID ?? "") },
  { key: "consignmentNo", label: "Consignment No", width: 160, text: (r) => r.temp?.sConsignmentNo || r.consignment?.sConsignmentNo || "" },
  { key: "vehicle", label: "Vehicle Reg No", width: 140, text: (r) => r.group.tVehicle?.sRegNo || "" },
  { key: "driver", label: "Driver Name", width: 140, text: (r) => r.group.tAssignDriver?.sName || "" },
  { key: "phone", label: "Driver Phone", width: 120, text: (r) => String(r.group.tAssignDriver?.aPhoneNo ?? "") },
  { key: "warehouse", label: "Warehouse", width: 170, text: (r) => r.consignment?.tWarehouse?.sName || "" },
  { key: "docNo", label: "Document No", width: 150, text: (r) => r.item?.sDocumentNo || "" },
  { key: "customer", label: "Customer", width: 210, text: (r) => r.item?.sBilltoName || "" },
  { key: "city", label: "City", width: 110, text: (r) => r.item?.sBilltocity || "" },
  { key: "address", label: "Address", width: 240, text: (r) => r.item?.sBilltoAddress || "" },
  {
    key: "items",
    label: "Item Count",
    width: 100,
    align: "right",
    filter: "number",
    text: (r) => String(r.consignment?.aQuantity ?? r.consignment?.tItems?.length ?? ""),
    num: (r) => toNum(r.consignment?.aQuantity ?? r.consignment?.tItems?.length),
  },
  {
    key: "amount",
    label: "Total Amount (₹)",
    width: 140,
    align: "right",
    filter: "number",
    text: (r) => (r.item?.aTotalInvAmount != null ? Number(r.item.aTotalInvAmount).toLocaleString("en-IN") : ""),
    num: (r) => toNum(r.item?.aTotalInvAmount),
  },
  {
    key: "delivery",
    label: "Delivery Date",
    width: 120,
    align: "center",
    filter: "date",
    text: (r) => (r.consignment?.dDeliveryDate ? fmtDate(r.consignment.dDeliveryDate) : ""),
    date: (r) => toDate(r.consignment?.dDeliveryDate),
  },
  {
    key: "status",
    label: "Status (at Recording)",
    width: 160,
    align: "center",
    text: (r) => (r.temp?.eStatus ? statusText(r.temp.eStatus) : (r.consignment ? statusText(r.consignment.eStatus) : "")),
    render: (r) => {
      const statusToUse = r.temp?.eStatus || r.consignment?.eStatus;
      return statusToUse ? (
        <span className={`erp-pill ${STATUS_CLASS[String(statusToUse)] || "st-secondary"}`}>
          {statusText(statusToUse)}
        </span>
      ) : (
        "-"
      );
    },
  },
  {
    key: "temp",
    label: "Temperature",
    width: 120,
    align: "right",
    filter: "temp",
    text: (r) => (r.temp ? fmtTemp(r.temp.sTemperature) : "No Data"),
    num: (r) => toNum(r.temp?.sTemperature),
    render: (r) => (
      <span className={tempClass(r.temp?.sTemperature)}>{r.temp ? fmtTemp(r.temp.sTemperature) : "No Data"}</span>
    ),
  },
  {
    key: "recorded",
    label: "Recorded At",
    width: 170,
    filter: "date",
    text: (r) => (r.temp?.createdAt ? fmtDateTime(r.temp.createdAt) : ""),
    date: (r) => toDate(r.temp?.createdAt),
  },
  { key: "location", label: "Location", width: 330, text: (r) => r.temp?.sLocation || "" },
  {
    key: "lat",
    label: "Latitude",
    width: 105,
    align: "right",
    filter: "number",
    text: (r) => (r.temp?.aLatitude != null ? Number(r.temp.aLatitude).toFixed(5) : ""),
    num: (r) => toNum(r.temp?.aLatitude),
  },
  {
    key: "lng",
    label: "Longitude",
    width: 105,
    align: "right",
    filter: "number",
    text: (r) => (r.temp?.aLongitude != null ? Number(r.temp.aLongitude).toFixed(5) : ""),
    num: (r) => toNum(r.temp?.aLongitude),
  },
  {
    key: "halt",
    label: "Halt Time",
    width: 140,
    text: (r) => (r.temp ? (r.temp.sHalt ? String(r.temp.sHalt) : "Moving") : ""),
    render: (r) =>
      r.temp ? (
        <span className={r.temp.sHalt ? "t-mid" : "t-ok"}>{r.temp.sHalt ? String(r.temp.sHalt) : "Moving"}</span>
      ) : (
        "-"
      ),
  },
  {
    key: "angle",
    label: "Angle (°)",
    width: 95,
    align: "right",
    filter: "number",
    text: (r) => (r.temp?.aAngle != null ? String(r.temp.aAngle) : ""),
    num: (r) => toNum(r.temp?.aAngle),
  },
];

const COL_BY_KEY: Record<string, Column> = COLUMNS.reduce<Record<string, Column>>((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

const defaultWidths = () =>
  COLUMNS.reduce<Record<string, number>>((acc, c) => {
    acc[c.key] = c.width;
    return acc;
  }, {});

const buildHay = (r: ReportRow) =>
  COLUMNS.filter((c) => c.key !== "sr")
    .map((c) => c.text(r, 0))
    .join("\u0001")
    .toLowerCase();

/* ------------------------------------------------------------------ */
/* Excel-style filter popover (opened from the header funnel icon)     */
/* ------------------------------------------------------------------ */

interface PopoverProps {
  col: Column;
  anchor: DOMRect;
  candidates: ReportRow[]; // rows that pass every OTHER filter
  filter: ColFilter;
  onChange: (f: ColFilter) => void;
  onClear: () => void;
  onClose: () => void;
}

const POPOVER_W = 280;

const FilterPopover: React.FC<PopoverProps> = ({ col, anchor, candidates, filter, onChange, onClear, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const kind: FilterKind = col.filter || "list";

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && ref.current.contains(t)) return;
      if (t.closest && t.closest("[data-filter-btn]")) return; // the funnel button toggles by itself
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  /* --- value list (kind = list) --- */
  const selected = filter.list;
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    if (kind === "list") {
      candidates.forEach((r) => {
        const v = col.text(r, 0);
        m.set(v, (m.get(v) || 0) + 1);
      });
      (selected || []).forEach((v) => {
        if (!m.has(v)) m.set(v, 0);
      });
    }
    return m;
  }, [candidates, col, kind, selected]);

  const shownValues = useMemo(() => {
    const all = Array.from(counts.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const terms = splitTerms(search);
    if (!terms.length) return all;
    return all.filter((v) => {
      const label = (v || "(blank)").toLowerCase();
      return terms.some((t) => label.indexOf(t) !== -1);
    });
  }, [counts, search]);

  const sel = selected || [];
  const toggle = (v: string) =>
    onChange({ ...filter, list: sel.indexOf(v) !== -1 ? sel.filter((s) => s !== v) : [...sel, v] });
  const selectAllShown = () => {
    const merged = [...sel];
    shownValues.forEach((v) => {
      if (merged.indexOf(v) === -1) merged.push(v);
    });
    onChange({ ...filter, list: merged });
  };

  const levels = filter.levels || [];
  const toggleLevel = (v: string) =>
    onChange({ ...filter, levels: levels.indexOf(v) !== -1 ? levels.filter((s) => s !== v) : [...levels, v] });

  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - POPOVER_W - 8));
  const top = Math.min(anchor.bottom + 2, Math.max(8, window.innerHeight - 440));

  return (
    <div className="fp" ref={ref} style={{ left, top, width: POPOVER_W }}>
      <div className="fp-title">{col.label}</div>

      {kind === "list" && (
        <>
          <input
            className="fp-search"
            autoFocus
            placeholder="Search — separate several values with , or ;"            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                selectAllShown();
              }
            }}
          />
          <div className="fp-actions">
            <button type="button" onClick={selectAllShown}>
              Select all shown ({shownValues.length})
            </button>
            <button type="button" onClick={() => onChange({ ...filter, list: [] })}>
              Unselect
            </button>
          </div>
          <div className="fp-list">
            {shownValues.slice(0, 300).map((v) => (
              <label key={v || "__blank"} className="fp-opt" title={v}>
                <input type="checkbox" checked={sel.indexOf(v) !== -1} onChange={() => toggle(v)} />
                <span className="fp-opt-text">{v || "(Blank)"}</span>
                <span className="fp-opt-count">{counts.get(v)}</span>
              </label>
            ))}
            {shownValues.length === 0 && <div className="fp-empty">No matching values</div>}
            {shownValues.length > 300 && <div className="fp-empty">Showing first 300. Type to narrow the list.</div>}
          </div>
          <div className="fp-note">Tick the values to show. Nothing ticked = no filter.</div>
        </>
      )}

      {(kind === "number" || kind === "temp") && (
        <>
          <div className="fp-row">
            <label>Min</label>
            <input type="number" value={filter.min || ""} onChange={(e) => onChange({ ...filter, min: e.target.value })} />
          </div>
          <div className="fp-row">
            <label>Max</label>
            <input type="number" value={filter.max || ""} onChange={(e) => onChange({ ...filter, max: e.target.value })} />
          </div>
        </>
      )}

      {kind === "temp" && (
        <div className="fp-levels">
          {TEMP_LEVEL_OPTIONS.map((o) => (
            <label key={o.value} className="fp-opt">
              <input type="checkbox" checked={levels.indexOf(o.value) !== -1} onChange={() => toggleLevel(o.value)} />
              <span className="fp-opt-text">{o.label}</span>
            </label>
          ))}
        </div>
      )}

      {kind === "date" && (
        <>
          <div className="fp-row">
            <label>From</label>
            <input type="date" value={filter.from || ""} onChange={(e) => onChange({ ...filter, from: e.target.value })} />
          </div>
          <div className="fp-row">
            <label>To</label>
            <input type="date" value={filter.to || ""} onChange={(e) => onChange({ ...filter, to: e.target.value })} />
          </div>
        </>
      )}

      <div className="fp-foot">
        <button type="button" className="fp-clear" onClick={onClear}>
          Clear filter
        </button>
        <button type="button" className="fp-done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const TripTemperatureReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [widths, setWidths] = useState<Record<string, number>>(defaultWidths);

  const [filters, setFilters] = useState<Record<string, ColFilter>>({});
  const [globalSearch, setGlobalSearch] = useState("");
  const [openFilter, setOpenFilter] = useState<{ key: string; rect: DOMRect } | null>(null);

  // Virtual scrolling state (only the rows inside the viewport are rendered)
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewH(el.clientHeight);
    update();
    const RO = (window as any).ResizeObserver;
    const ro = RO ? new RO(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  /* ---------- data ---------- */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Only fetch location tracker data - this is the only data we should show
      const locRes = await getLocationTrackerData();
      const tracks: any[] = locRes.data || [];

      // If no data in locationTracker collection, show empty
      if (tracks.length === 0) {
        setRows([]);
        console.info('[TripTemperatureReport] No data in locationTracker collection');
        return;
      }

      // Index tracker records by consignment ID
      const byConsignmentId = new Map<string, any[]>();
      tracks.forEach((t) => {
        if (t.sConsignmentId) {
          const id = String(t.sConsignmentId);
          if (!byConsignmentId.has(id)) {
            byConsignmentId.set(id, []);
          }
          byConsignmentId.get(id)!.push(t);
        }
      });

      // Fetch group consignments to get details for the tracker records
      const groupRes = await getGroupConsignments();
      const groups: any[] = groupRes.data || [];

      const flat: ReportRow[] = [];
      let totalLogs = 0;

      // Only create rows for tracker records that exist in the database
      byConsignmentId.forEach((logs, consignmentId) => {
        const group = groups.find(g => g.grpConsignmentID === consignmentId);

        if (group) {
          const consignments: any[] = group.tConsignments?.length ? group.tConsignments : [];

          // Newest log first
          const sortedLogs = logs
            .slice()
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

          sortedLogs.forEach((log, li) => {
            totalLogs += 1;

            // Use the sConsignmentNo from the tracker record to find the matching consignment
            const matchingConsignment = consignments.find(c => c.sConsignmentNo === log.sConsignmentNo);
            const consignment = matchingConsignment || consignments[0] || null;
            const item = consignment?.tItems?.[0] || null;

            flat.push({
              id: `${consignmentId}-${log.sConsignmentNo}-${li}`,
              groupKey: consignmentId,
              group,
              consignment,
              item,
              temp: log,
              hay: "",
              hayN: ""
            });
          });
        }
      });

      console.info(
        `[TripTemperatureReport] grid rows: ${flat.length}, total logs shown: ${totalLogs}, tracker records loaded: ${tracks.length}`
      );
      setRows(flat);
    } catch (e) {
      console.error("Error fetching data:", e);
      setError("Failed to load data. Please try again later.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ---------- filtering ---------- */
  const globalTerms = useMemo(() => splitTerms(globalSearch), [globalSearch]);

  /** Does a row pass the global search and every column filter (optionally ignoring one column)? */
  const rowPasses = useCallback(
    (r: ReportRow, skipKey?: string) => {
            if (globalTerms.length) {
        if (!r.hay) {
          r.hay = buildHay(r);
          r.hayN = squash(r.hay);
        }
        const ok = globalTerms.some((t) => {
          if (r.hay.indexOf(t) !== -1) return true;
          const tn = squash(t);
          return tn.length > 0 && r.hayN.indexOf(tn) !== -1;
        });
        if (!ok) return false;
      }

      for (const key of Object.keys(filters)) {
        if (key === skipKey) continue;
        const f = filters[key];
        const c = COL_BY_KEY[key];
        if (!c) continue;

        if (f.list && f.list.length && f.list.indexOf(c.text(r, 0)) === -1) return false;

        if (f.min || f.max) {
          const n = c.num ? c.num(r) : null;
          const lo = toNum(f.min);
          const hi = toNum(f.max);
          if (n === null) return false;
          if (lo !== null && n < lo) return false;
          if (hi !== null && n > hi) return false;
        }

        if (f.from || f.to) {
          const d = c.date ? c.date(r) : null;
          if (!d) return false;
          if (f.from && d < new Date(`${f.from}T00:00:00`)) return false;
          if (f.to && d > new Date(`${f.to}T23:59:59`)) return false;
        }

        if (f.levels && f.levels.length) {
          if (f.levels.indexOf(tempLevel(toNum(r.temp?.sTemperature))) === -1) return false;
        }
      }
      return true;
    },
    [filters, globalTerms]
  );

  const filteredRows = useMemo(() => rows.filter((r) => rowPasses(r)), [rows, rowPasses]);

  // Values offered in the open popover come from rows that pass all the other filters
  const openCandidates = useMemo(
    () => (openFilter ? rows.filter((r) => rowPasses(r, openFilter.key)) : []),
    [openFilter, rows, rowPasses]
  );

  const setColFilter = (key: string, f: ColFilter) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (isActive(f)) next[key] = f;
      else delete next[key];
      return next;
    });
  };

  const clearAll = () => {
    setFilters({});
    setGlobalSearch("");
    setOpenFilter(null);
  };

  const closeFilter = useCallback(() => setOpenFilter(null), []);

  const toggleFilter = (e: React.MouseEvent<HTMLButtonElement>, key: string) => {
    e.stopPropagation();
    if (openFilter && openFilter.key === key) {
      setOpenFilter(null);
    } else {
      setOpenFilter({ key, rect: e.currentTarget.getBoundingClientRect() });
    }
  };

  const summarize = (f: ColFilter) => {
    const parts: string[] = [];
    if (f.list && f.list.length) parts.push(f.list.length === 1 ? f.list[0] || "(Blank)" : `${f.list.length} values`);
    if (f.min || f.max) parts.push(`${f.min || "…"} to ${f.max || "…"}`);
    if (f.from || f.to) parts.push(`${f.from || "…"} → ${f.to || "…"}`);
    if (f.levels && f.levels.length) parts.push(f.levels.join(", "));
    return parts.join("; ");
  };

  const activeKeys = Object.keys(filters);

  /* ---------- column resize (drag header edge like Excel) ---------- */
  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key];
    const onMove = (ev: MouseEvent) => {
      setWidths((w) => ({ ...w, [key]: Math.max(60, startW + ev.clientX - startX) }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.classList.remove("erp-resizing");
    };
    document.body.classList.add("erp-resizing");
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resetColumnWidth = (key: string) => {
    const def = COLUMNS.find((c) => c.key === key)?.width || 100;
    setWidths((w) => ({ ...w, [key]: def }));
  };

  const totalWidth = COLUMNS.reduce((sum, c) => sum + (widths[c.key] || c.width), 0);

  /* ---------- virtual window ---------- */
  const total = filteredRows.length;
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(total, Math.ceil((scrollTop + viewH) / ROW_H) + OVERSCAN);
  const visibleRows = filteredRows.slice(start, end);
  const topPad = start * ROW_H;
  const bottomPad = (total - end) * ROW_H;

  /* ---------- export (only the currently filtered rows) ---------- */
  const buildMatrix = () => ({
    head: COLUMNS.map((c) => c.label),
    body: filteredRows.map((r, i) => COLUMNS.map((c) => c.text(r, i))),
  });

  const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

   const exportToExcel = () => {
    if (!filteredRows.length) return;

    const thin = { style: "thin", color: { rgb: "D0D7DE" } };
    const border = { top: thin, bottom: thin, left: thin, right: thin };
    const FONT = "Calibri";

    // Excel number formats for numeric columns (values are written as real numbers)
    const numFmt: Record<string, string> = {
      sr: "0",
      items: "0",
      amount: "#,##0.00",
      temp: '0.0"°C"',
      lat: "0.00000",
      lng: "0.00000",
      angle: "0",
    };
    const dateFmt: Record<string, string> = {
      delivery: "dd/mm/yyyy",
      recorded: "dd/mm/yyyy hh:mm:ss",
    };
    const toSerial = (d: Date) => (d.getTime() - d.getTimezoneOffset() * 60000) / 86400000 + 25569;

    // Colours: [background, text]
    const TEMP_COLORS: Record<string, [string, string]> = {
      normal: ["C6EFCE", "006100"],
      warning: ["FFEB9C", "9C5700"],
      high: ["FFC7CE", "9C0006"],
      nodata: ["EDEDED", "595959"],
    };
    const STATUS_COLORS: Record<string, [string, string]> = {
      "0": ["DDEBF7", "1F4E79"],
      "1": ["BDD7EE", "1F3864"],
      "2": ["C6EFCE", "006100"],
      "3": ["FFEB9C", "9C5700"],
      "4": ["FFC7CE", "9C0006"],
    };

    const ws: any = {};
    const put = (r: number, c: number, cell: any) => {
      ws[XLSX.utils.encode_cell({ r, c })] = cell;
    };
    const lastCol = COLUMNS.length - 1;

    /* ---- Row 0: title (merged) ---- */
    const titleStyle = {
      font: { name: FONT, sz: 16, bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1F3864" } },
      alignment: { horizontal: "left", vertical: "center", indent: 1 },
    };
    COLUMNS.forEach((_, c) => put(0, c, { t: "s", v: c === 0 ? "Trip Temperature Report" : "", s: titleStyle }));

    /* ---- Row 1: generated info + active filters (merged) ---- */
    const filterText = activeKeys.length
      ? activeKeys.map((k) => `${COL_BY_KEY[k]?.label}: ${summarize(filters[k])}`).join(" | ")
      : "None";
    const info =
      `Generated: ${new Date().toLocaleString("en-GB")}    |    Rows: ${filteredRows.length}` +
      `    |    Search: ${globalSearch.trim() || "None"}    |    Filters: ${filterText}`;
    const infoStyle = {
      font: { name: FONT, sz: 10, italic: true, color: { rgb: "1F3864" } },
      fill: { fgColor: { rgb: "DDEBF7" } },
      alignment: { horizontal: "left", vertical: "center", indent: 1 },
    };
    COLUMNS.forEach((_, c) => put(1, c, { t: "s", v: c === 0 ? info : "", s: infoStyle }));

    /* ---- Row 3: column headers ---- */
    const HEADER_ROW = 3;
    const headStyle = {
      font: { name: FONT, sz: 11, bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "2F5597" } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border: {
        top: { style: "thin", color: { rgb: "1F3864" } },
        bottom: { style: "medium", color: { rgb: "1F3864" } },
        left: { style: "thin", color: { rgb: "8EA9DB" } },
        right: { style: "thin", color: { rgb: "8EA9DB" } },
      },
    };
    COLUMNS.forEach((c, ci) => put(HEADER_ROW, ci, { t: "s", v: c.label, s: headStyle }));

    /* ---- Body ---- */
    filteredRows.forEach((r, i) => {
      const row = HEADER_ROW + 1 + i;
      const zebra = i % 2 === 1 ? "F2F6FC" : "FFFFFF";
      const groupStart = i === 0 || filteredRows[i - 1].groupKey !== r.groupKey;

      COLUMNS.forEach((c, ci) => {
        let fill = zebra;
        let color = "000000";
        let bold = false;
        let horizontal: string = c.align || "left";

        // Value: real number / real date where possible, otherwise text
        let cell: any;
        if (c.key === "sr") {
          cell = { t: "n", v: i + 1, z: numFmt.sr };
        } else if (numFmt[c.key] && c.num && c.num(r) !== null) {
          cell = { t: "n", v: c.num(r), z: numFmt[c.key] };
        } else if (dateFmt[c.key] && c.date && c.date(r)) {
          cell = { t: "n", v: toSerial(c.date(r) as Date), z: dateFmt[c.key] };
        } else {
          cell = { t: "s", v: c.text(r, i) || "-" };
        }

        // Colour-coded cells
        if (c.key === "temp") {
          const [bg, fg] = TEMP_COLORS[tempLevel(toNum(r.temp?.sTemperature))];
          fill = bg;
          color = fg;
          bold = true;
        } else if (c.key === "status" && r.consignment) {
          const [bg, fg] = STATUS_COLORS[String(r.consignment.eStatus)] || ["EDEDED", "595959"];
          fill = bg;
          color = fg;
          bold = true;
          horizontal = "center";
        } else if (c.key === "halt" && r.temp) {
          color = r.temp.sHalt ? "9C5700" : "006100";
        }

        cell.s = {
          font: { name: FONT, sz: 10, bold, color: { rgb: color } },
          fill: { fgColor: { rgb: fill } },
          alignment: { horizontal, vertical: "center" },
          border: groupStart
            ? { ...border, top: { style: "medium", color: { rgb: "8EA9DB" } } } // thicker line between consignments
            : border,
        };
        put(row, ci, cell);
      });
    });

    /* ---- Sheet settings ---- */
    const lastRow = HEADER_ROW + filteredRows.length;
    ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: lastRow, c: lastCol } });
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
    ];
    ws["!cols"] = COLUMNS.map((c) => ({ wch: Math.max(10, Math.round((widths[c.key] || c.width) / 7)) }));
    ws["!rows"] = [{ hpt: 30 }, { hpt: 20 }, { hpt: 6 }, { hpt: 32 }];
    ws["!autofilter"] = { ref: `A${HEADER_ROW + 1}:${XLSX.utils.encode_col(lastCol)}${lastRow + 1}` };
    ws["!freeze"] = { xSplit: "1", ySplit: String(HEADER_ROW + 1), topLeftCell: "B" + (HEADER_ROW + 2), activePane: "bottomRight", state: "frozen" };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Trip Temperature");
    XLSX.writeFile(wb, `trip_temperature_report_${stamp()}.xlsx`);
  };

  
  const exportToPDF = () => {
    if (!filteredRows.length) return;
    const { head, body } = buildMatrix();
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFontSize(14);
    doc.text("Trip Temperature Report", 30, 30);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString("en-GB")}    Rows: ${filteredRows.length}`, 30, 46);

    const colStyles: Record<number, any> = {};
    COLUMNS.forEach((c, i) => {
      colStyles[i] = { halign: c.align || "left" };
    });

    autoTable(doc, {
      head: [head],
      body,
      startY: 56,
      styles: { fontSize: 6, cellPadding: 2.5, overflow: "linebreak", lineWidth: 0.3, lineColor: [200, 205, 210] },
      headStyles: { fillColor: [41, 62, 88], textColor: 255, fontSize: 6, halign: "center" },
      alternateRowStyles: { fillColor: [246, 248, 250] },
      columnStyles: colStyles,
      margin: { left: 20, right: 20 },
      didDrawPage: () => {
        const p = (doc as any).internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Page ${p}`, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 14);
      },
    });
    doc.save(`trip_temperature_report_${stamp()}.pdf`);
  };

  /* ---------- render ---------- */
  const anyFilter = activeKeys.length > 0 || globalTerms.length > 0;

  return (
    <div className="ttr-page">
      {/* Toolbar */}
      <div className="ttr-toolbar">
        <div className="ttr-title">Trip Temperature Report</div>
                <input
          className="ttr-search"
          placeholder="Search all columns… (separate several values with , or ;)"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[\r\n\t]/.test(text)) {
              e.preventDefault();
              const joined = text.trim().split(/[\r\n\t]+/).join(", ");
              setGlobalSearch((prev) => (prev ? `${prev}, ${joined}` : joined));
            }
          }}
        />
        <div className="ttr-actions">
  <Button variant="outline-secondary" size="sm" onClick={clearAll} disabled={!anyFilter} title="Clear filters">
    <span className="btn-label">Clear filters{activeKeys.length ? ` (${activeKeys.length})` : ""}</span>
    <span className="d-sm-none">✕{activeKeys.length ? ` ${activeKeys.length}` : ""}</span>
  </Button>
  <Button variant="outline-secondary" size="sm" onClick={fetchData} disabled={loading} title="Refresh">
    <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
    <span className="btn-label">Refresh</span>
  </Button>
  <Button variant="success" size="sm" onClick={exportToExcel} disabled={!filteredRows.length} title="Export Excel">
    <FontAwesomeIcon icon={faFileExcel} className="me-1" />
    <span className="btn-label">Export Excel</span>
  </Button>
  <Button variant="danger" size="sm" onClick={exportToPDF} disabled={!filteredRows.length} title="Export PDF">
    <FontAwesomeIcon icon={faFilePdf} className="me-1" />
    <span className="btn-label">Export PDF</span>
  </Button>
</div>
      </div>

      {/* Active filters */}
      {activeKeys.length > 0 && (
        <div className="ttr-active">
          <span className="ttr-active-label">Filters:</span>
          {activeKeys.map((k) => (
            <span className="ttr-chip" key={k} title={summarize(filters[k])}>
              <b>{COL_BY_KEY[k]?.label}</b>: {summarize(filters[k])}
              <button type="button" aria-label={`Remove ${COL_BY_KEY[k]?.label} filter`} onClick={() => setColFilter(k, {})}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="ttr-alert">
          {error}
        </Alert>
      )}

      {/* Grid */}
      <div className="ttr-grid-wrap">
        {loading && (
          <div className="ttr-loading">
            <Spinner animation="border" size="sm" /> Loading data...
          </div>
        )}
        <div className="ttr-grid-scroll" ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
          <table className="erp-grid" style={{ width: totalWidth }}>
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: widths[c.key] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => {
                  const filtered = !!filters[c.key];
                  return (
                    <th
                      key={c.key}
                      className={`${c.key === "sr" ? "col-sr" : ""} ${filtered ? "filtered" : ""}`}
                      title={c.label}
                    >
                      <div className="th-inner">
                        <span className="th-label">{c.label}</span>
                        {c.filter !== "none" && (
                          <button
                            type="button"
                            data-filter-btn
                            className={`th-filter ${filtered ? "on" : ""}`}
                            title="Filter this column"
                            onClick={(e) => toggleFilter(e, c.key)}
                          >
                            <FontAwesomeIcon icon={faFilter} />
                          </button>
                        )}
                      </div>
                      <span
                        className="col-resizer"
                        onMouseDown={(e) => startResize(e, c.key)}
                        onDoubleClick={() => resetColumnWidth(c.key)}
                        title="Drag to resize · Double-click to reset"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {total > 0 ? (
                <>
                  {topPad > 0 && (
                    <tr className="spacer" aria-hidden="true" style={{ height: topPad }}>
                      <td colSpan={COLUMNS.length} />
                    </tr>
                  )}
                  {visibleRows.map((r, vi) => {
                    const idx = start + vi;
                    const prev = idx > 0 ? filteredRows[idx - 1] : null;
                    const cls = `${idx % 2 ? "alt" : ""} ${!prev || prev.groupKey !== r.groupKey ? "grp-start" : ""}`;
                    return (
                      <tr key={r.id} className={cls}>
                        {COLUMNS.map((c) => (
                          <td
                            key={c.key}
                            className={c.key === "sr" ? "col-sr" : ""}
                            style={{ textAlign: c.align || "left" }}
                            title={c.text(r, idx)}
                          >
                            {c.render ? c.render(r, idx) : c.text(r, idx) || "-"}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {bottomPad > 0 && (
                    <tr className="spacer" aria-hidden="true" style={{ height: bottomPad }}>
                      <td colSpan={COLUMNS.length} />
                    </tr>
                  )}
                </>
              ) : (
                !loading && (
                  <tr className="no-data-row">
                    <td colSpan={COLUMNS.length}>No data found for the selected filters</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        <div className="ttr-statusbar">
          <span>
            Showing {total} of {rows.length} rows (one row per temperature log)
          </span>
          <span>Click the funnel on a column header to filter · Drag a header edge to resize · Double-click the edge to reset</span>
        </div>
      </div>

      {openFilter &&
        createPortal(
          <FilterPopover
            key={openFilter.key}
            col={COL_BY_KEY[openFilter.key]}
            anchor={openFilter.rect}
            candidates={openCandidates}
            filter={filters[openFilter.key] || {}}
            onChange={(f) => setColFilter(openFilter.key, f)}
            onClear={() => setColFilter(openFilter.key, {})}
            onClose={closeFilter}
          />,
          document.body
        )}
    </div>
  );
};

export default TripTemperatureReport;